import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableSortLabel,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import { Add, Search, Clear, FilterList } from "@material-ui/icons";
import { useLazyQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { GET_SERVICE_REQUESTS } from "../../../../graphql/serviceRequests/getServiceRequestsQuery";
import { getLocalizedDateFormat } from "../../../../utils/localized.syntex";
import { DATE_TYPE } from "../../../utils";
import { IServiceRequest, ServiceRequestStatus } from "./types";
import { DateTime } from "luxon";
import { StyledTableCell, StyledTableRow } from "../../../StyledTable";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { IAppState } from "../../../../store";

const useStyles = makeStyles((theme) => ({
  chip: {
    fontSize: "12px",
    height: "24px",
    borderRadius: "12px",
    fontWeight: 500
  },
  filterCard: {
    padding: "16px",
    marginBottom: "16px",
    backgroundColor: "#f5f5f5"
  },
  tabs: {
    marginBottom: "16px",
    backgroundColor: "#fff",
    borderRadius: "4px"
  },
  statusChip: {
    fontWeight: 600,
    fontSize: "11px"
  }
}));

const ServiceRequestsList: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();
  const user = useSelector((state: IAppState) => state.authReducer.user);


  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [searchableColumns] = useState<string[]>([
    "referenceNumber",
    "vehicleId",
    "status"
  ]);
  const [sortBy, setSortBy] = useState("updatedAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [serviceRequests, setServiceRequests] = useState<IServiceRequest[]>([]);
  const [tempStatuses, setTempStatuses] = useState<string[]>([]);

  useEffect(() => {
    if (filterModalOpen) {
      setTempStatuses(selectedStatuses);
    }
  }, [filterModalOpen, selectedStatuses]);
  
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput);
        setPage(0);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput, searchTerm]);

  const [loadServiceRequests, { loading, error }] = useLazyQuery(GET_SERVICE_REQUESTS, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.consumerServiceRequests) {
        setServiceRequests(data.consumerServiceRequests.serviceRequests || []);
        setTotalCount(data.consumerServiceRequests.total || 0);
      }
    }
  });

  useEffect(() => {
    const offset = page * rowsPerPage;
    
    const statusArray = selectedStatuses.length > 0 ? selectedStatuses : undefined;
    const userBusinessCustomerId = user?.businesscustomers?.id;
    const userIndividualCustomerId = user?.customerId;

    const filtersPayload: any = {
      status: statusArray,
      searchTerm: searchTerm || undefined,
      searchColumns: searchableColumns.length > 0 ? searchableColumns : undefined
    };

    if (userBusinessCustomerId){
      filtersPayload.businessCustomer = userBusinessCustomerId;
    } else if (userIndividualCustomerId) {
      filtersPayload.customer = userIndividualCustomerId;
    }

    const variables = {
      args: {
        filters: filtersPayload,
        limit: rowsPerPage,
        offset: offset,
        sortBy: sortBy,
        sortOrder: sortOrder
      }
    };
    
    loadServiceRequests({ variables });
  }, [page, rowsPerPage, searchTerm, selectedStatuses, sortBy, sortOrder, searchableColumns, loadServiceRequests, user]);

  const getStatusColor = (status: ServiceRequestStatus): "default" | "primary" | "secondary" => {
    switch (status) {
      case ServiceRequestStatus.APPROVED:
      case ServiceRequestStatus.COMPLETED:
        return "primary";
      case ServiceRequestStatus.IN_PROGRESS:
        return "secondary";
      case ServiceRequestStatus.PENDING_APPROVAL:
      case ServiceRequestStatus.AWAITING_MORE_INFORMATION:
      case ServiceRequestStatus.DECLINED:
      default:
        return "default";
    }
  };

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  }, []);

  const handleStatusFilterChange = useCallback((event: any) => {
    const value = event.target.value as string[];
    setTempStatuses(value);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setSelectedStatuses(tempStatuses);
    setPage(0);
    setFilterModalOpen(false);
  }, [tempStatuses]);

  const handleResetFilters = useCallback(() => {
    setSelectedStatuses([]);
    setTempStatuses([]);
    setPage(0);
  }, []);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  }, []);

  const handleSort = useCallback((column: string) => {
    const isAsc = sortBy === column && sortOrder === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setSortOrder(newOrder);
    setSortBy(column);
    setPage(0);
  }, [sortBy, sortOrder]);

  const handleClearFilters = useCallback(() => {
    setSearchInput("");
    setSearchTerm("");
    setSelectedStatuses([]);
    setTempStatuses([]);
    setPage(0);
  }, []);

  const handleViewDetails = useCallback((serviceRequestId: string) => {
    history.push(`/account/service-request/${serviceRequestId}`);
  }, [history]);

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{t("snackBar:errorLoadingServiceRequests")}: {error.message}</Typography>
      </Box>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: "24px" }}>
        <Typography variant="h3" style={{ fontWeight: 600, fontSize: "2rem" }}>
          {t("root:serviceRequests")}
        </Typography>

        <Box display="flex" alignItems="center" style={{ gap: "16px" }}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setFilterModalOpen(true)}
            style={{
              height: "40px",
              textTransform: "none",
              borderColor: selectedStatuses.length > 0 ? "#1976d2" : "rgba(0, 0, 0, 0.23)",
              color: selectedStatuses.length > 0 ? "#1976d2" : "inherit"
            }}
          >
            {t("root:filters")} {selectedStatuses.length > 0 && `(${selectedStatuses.length})`}
          </Button>

          {selectedStatuses.length > 0 && (
            <Button 
              variant="outlined" 
              startIcon={<Clear />} 
              onClick={handleClearFilters}
              style={{ 
                whiteSpace: "nowrap",
                height: "40px",
                textTransform: "none",
                borderColor: "rgba(0, 0, 0, 0.23)"
              }}
            >
              {t("root:clear")} {t("root:all")}
            </Button>
          )}

          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => history.push("/account/service-request/new")}
            style={{
              backgroundColor: "#1976d2",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              height: "40px"
            }}
          >
            {t("root:newServiceRequest")}
          </Button>
        </Box>
      </Box>

      <Dialog
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" style={{ fontWeight: 600 }}>{t("root:filters").toUpperCase()}</Typography>
            <Box>
              <Button
                color="primary"
                onClick={handleResetFilters}
                style={{ textTransform: "none", marginRight: 8 }}
              >
                {t("root:reset").toUpperCase()}
              </Button>
              <IconButton size="small" onClick={() => setFilterModalOpen(false)}>
                <Clear />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>{t("root:status")}</InputLabel>
                <Select
                  multiple
                  value={tempStatuses}
                  onChange={handleStatusFilterChange}
                  MenuProps={{
                    getContentAnchorEl: null
                  }}
                  input={<OutlinedInput label={t("root:status")} />}
                  renderValue={(selected) => 
                    (selected as string[]).length === 0 ? t("root:all") : (selected as string[]).join(", ")
                  }
                >
                  <MenuItem value={ServiceRequestStatus.PENDING_APPROVAL}>
                    <Checkbox checked={tempStatuses.includes(ServiceRequestStatus.PENDING_APPROVAL)} />
                    <ListItemText primary={t("root:pendingApproval")} />
                  </MenuItem>
                  <MenuItem value={ServiceRequestStatus.APPROVED}>
                    <Checkbox checked={tempStatuses.includes(ServiceRequestStatus.APPROVED)} />
                    <ListItemText primary={t("root:approved")} />
                  </MenuItem>
                  <MenuItem value={ServiceRequestStatus.DECLINED}>
                    <Checkbox checked={tempStatuses.includes(ServiceRequestStatus.DECLINED)} />
                    <ListItemText primary={t("misc:status.DECLINED")} />
                  </MenuItem>
                  <MenuItem value={ServiceRequestStatus.IN_PROGRESS}>
                    <Checkbox checked={tempStatuses.includes(ServiceRequestStatus.IN_PROGRESS)} />
                    <ListItemText primary={t("root:inProgress")} />
                  </MenuItem>
                  <MenuItem value={ServiceRequestStatus.AWAITING_MORE_INFORMATION}>
                    <Checkbox checked={tempStatuses.includes(ServiceRequestStatus.AWAITING_MORE_INFORMATION)} />
                    <ListItemText primary={t("root:awaitingMoreInformation")} />
                  </MenuItem>
                  <MenuItem value={ServiceRequestStatus.COMPLETED}>
                    <Checkbox checked={tempStatuses.includes(ServiceRequestStatus.COMPLETED)} />
                    <ListItemText primary={t("root:completed")} />
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
          onClick={handleApplyFilters} color="primary" variant="contained"
          disabled={tempStatuses.length === 0}  
          >
            {t("root:applyFilters")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filters Section */}
      <Card className={classes.filterCard}>
        <Grid container spacing={2} alignItems="center">
          {/* Active Filters Display */}
          {selectedStatuses.length > 0 && (
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: "8px" }}>
                <Typography variant="body2" style={{ fontWeight: 600, marginRight: 8 }}>
                  {t("root:activeFilters")}:
                </Typography>
                {selectedStatuses.map((status) => (
                  <Chip
                    key={`status-${status}`}
                    label={`${t("root:status")}: ${status}`}
                    onDelete={() => {
                      setSelectedStatuses(selectedStatuses.filter(s => s !== status));
                      setPage(0);
                    }}
                    color="primary"
                    size="small"
                    className={classes.chip}
                  />
                ))}
              </Box>
            </Grid>
          )}
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("root:searchServiceRequestsPlaceholder")}
              value={searchInput}
              onChange={handleSearchChange}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchInput("");
                        setSearchTerm("");
                      }}
                      edge="end"
                    >
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>
      </Card>

      <Box style={{ position: 'relative' }}>
        {loading && (
          <Box
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1000
            }}
          >
            <CircularProgress />
          </Box>
        )}
        
        <TableContainer style={{ maxHeight: "calc(100vh - 400px)", minHeight: "400px" }} className="row-hover">
          <Table stickyHeader aria-label="service requests table">
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell>
                    <TableSortLabel
                      active={sortBy === "referenceNumber"}
                      direction={sortBy === "referenceNumber" ? sortOrder : "asc"}
                      onClick={() => handleSort("referenceNumber")}
                    >
                      {t("root:referenceNumber")}
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell>{t("root:vehicle")}</StyledTableCell>
                  <StyledTableCell>
                    <TableSortLabel
                      active={sortBy === "status"}
                      direction={sortBy === "status" ? sortOrder : "asc"}
                      onClick={() => handleSort("status")}
                    >
                      {t("root:status")}
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell>
                    <TableSortLabel
                      active={sortBy === "updatedAt"}
                      direction={sortBy === "updatedAt" ? sortOrder : "asc"}
                      onClick={() => handleSort("updatedAt")}
                    >
                      {t("root:lastUpdated", { defaultValue: "Last updated" })}
                    </TableSortLabel>
                  </StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {serviceRequests.length ? (
                  serviceRequests.map((request) => (
                    <StyledTableRow
                      key={request.id}
                      onClick={() => handleViewDetails(request.id)}
                      style={{ cursor: "pointer" }}
                      hover
                    >
                      <StyledTableCell>
                        <span style={{ color: "rgb(24, 117, 210)", fontWeight: 600 }}>
                          {request.referenceNumber}
                        </span>
                      </StyledTableCell>
                      <StyledTableCell>
                        {request.vehicle?.licencePlate || request.vehicleId || "N/A"}
                      </StyledTableCell>
                      <StyledTableCell>
                        <Chip
                          label={request.status?.replace(/_/g, " ")}
                          color={getStatusColor(request.status as ServiceRequestStatus)}
                          size="small"
                          className={classes.chip}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        {DateTime.fromISO(request.updatedAt || request.createdAt).toFormat("MMM dd, yyyy")}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <StyledTableRow>
                    <StyledTableCell colSpan={4}>
                      <Typography style={{ textAlign: "center", opacity: 0.5, padding: "40px 0" }}>
                        {t("root:noServiceRequestsFound", "No service requests found")}
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

        <Box style={{ borderTop: '1px solid #e0e0e0', paddingTop: '8px' }}>
          <TablePagination
            key={`pagination-${page}-${rowsPerPage}-${totalCount}`}
            rowsPerPageOptions={[10, 20, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            labelRowsPerPage={t("root:rowsPerPage")}
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} ${t("root:of")} ${count !== -1 ? count : `${t("root:moreThan")} ${to}`}`
            }
            SelectProps={{
              inputProps: { 'aria-label': 'rows per page' },
              native: true,
            }}
          />
        </Box>
      </Box>
    </div>
  );
};

export default ServiceRequestsList;

